// ============================================================================
// Beacon — Community. A network of named syndicate groups that share KNOWLEDGE
// (best practices, pitfalls, playbooks, market insight) and property LEADS.
//
// COMPLIANCE IS NON-NEGOTIABLE. Every member must read and SIGN the Community
// Compact (do's & don'ts) before entering. The whole surface is a knowledge +
// LEADS exchange only — never a place to solicit securities across groups or to
// pay/accept deal commissions. Beacon holds no funds and facilitates no
// transactions. Guardrails + disclaimers are enforced in the UI and recorded.
// Same dual-mode repo pattern (cloud tables + local fallback).
// ============================================================================

import { getSupabase, isCloud } from "./supabase";

export const COMPACT_VERSION = "1.0";

/** The rules every member commits to — the heart of the compliance gate. */
export const COMPACT_DOS: string[] = [
  "Share knowledge, lessons, pitfalls and playbooks in good faith — to help the whole community learn.",
  "Keep your own group's investment offering PRIVATE to your own circle of people you already know.",
  "Share property LEADS — and always do your own due diligence and use your own attorney and CPA.",
  "Treat everything here as peer experience, not professional legal, tax, or investment advice.",
  "Respect privacy — post only information you have the right to share.",
];

export const COMPACT_DONTS: string[] = [
  "Do NOT solicit or advertise any investment or securities offering to members outside your own group. (This is general solicitation — it breaks the private-offering exemption.)",
  "Do NOT offer, pay, or accept any commission, referral fee, or kickback for a real-estate deal on this platform. (Real-estate referral fees require a license; kickbacks violate RESPA.)",
  "Do NOT give legal, tax, or investment advice, or present yourself as licensed if you are not.",
  "Do NOT share confidential, personal, or non-public information you don't have the right to share.",
  "Do NOT post deals or claims you cannot substantiate. No misrepresentation, ever.",
  "Do NOT use the community to raise money from the general public.",
];

/** The signed commitment. */
export const PLEDGE_TEXT =
  "I understand Beacon is software — not a broker-dealer, funding portal, law firm, or real-estate brokerage — and " +
  "that it does not give legal, tax, or investment advice, does not hold any funds, and does not facilitate securities " +
  "transactions. I have read the Do's & Don'ts above and I commit to 100% compliance: I will keep my group's offering " +
  "private to my own circle, I will not solicit outside investors or pay/accept any deal commission on this platform, " +
  "and I will engage my own attorney and CPA. I share knowledge and leads in good faith and at my own risk.";

export const DISCLAIMERS = {
  knowledge: "Peer knowledge shared in good faith — not legal, tax, or investment advice. Verify everything independently.",
  leads: "Property leads only. No securities are offered here, and no commissions are paid or accepted on this platform. Do your own due diligence and engage your own attorney.",
  privacy: "Posts are visible to other members of the community. Share only what you have the right to share — never confidential, personal, or non-public information.",
  footer: "Beacon is software; it holds no funds and facilitates no transactions. Each group runs its own private offering with its own counsel.",
};

export const POST_CATEGORIES = ["Best practice", "Pitfall / lesson", "Playbook", "Market insight", "Question", "Win"] as const;

// --- data types -------------------------------------------------------------
export interface Pledge { id: string; name: string; version: string; acceptedAt: string; }
export interface CommunityPost {
  id: string; group: string; author: string; category: string; title: string; body: string; created_at?: string;
}
export interface CommunityLead {
  id: string; group: string; author: string; market: string; address?: string; ask?: number; note: string; contact?: string; created_at?: string;
}

let counter = 0;
const newId = (p: string) => { counter += 1; return p + Date.now().toString(36) + counter.toString(36); };
export const newPostId = () => newId("cp");
export const newLeadId = () => newId("cl");
export const newRequestId = () => newId("cr");

// --- community requests (course / feature suggestions + voting) -------------
export type RequestKind = "course" | "feature";
export type RequestStatus = "open" | "planned" | "shipped";
export interface CommunityRequest {
  id: string; kind: RequestKind; title: string; detail?: string; author?: string;
  votes: number; status: RequestStatus; created_at?: string;
}

// --- pledge (per member, versioned) -----------------------------------------
const PLEDGE_KEY = "beacon_compact_" + COMPACT_VERSION;
export function hasAcceptedCompact(): boolean {
  try { return !!localStorage.getItem(PLEDGE_KEY); } catch { return false; }
}
export function acceptedName(): string | null {
  try { return localStorage.getItem(PLEDGE_KEY); } catch { return null; }
}
export async function acceptCompact(name: string): Promise<void> {
  try { localStorage.setItem(PLEDGE_KEY, name); } catch { /* ignore */ }
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      await sb.from("community_pledges").upsert({ id: newId("pl"), name, version: COMPACT_VERSION, acceptedAt: new Date().toISOString() });
    } catch { /* best-effort record */ }
  }
}

// --- posts (knowledge) ------------------------------------------------------
const LS_POSTS = "beacon_community_posts";
function localPosts(): CommunityPost[] { try { const r = localStorage.getItem(LS_POSTS); if (r) return JSON.parse(r); } catch { /* */ } return []; }
function saveLocalPosts(a: CommunityPost[]) { try { localStorage.setItem(LS_POSTS, JSON.stringify(a)); } catch { /* */ } }

export async function listPosts(): Promise<CommunityPost[]> {
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { data, error } = await sb.from("community_posts").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      if (data) return data as CommunityPost[];
    } catch { /* fall through */ }
  }
  return localPosts().slice().reverse();
}
export async function addPost(p: CommunityPost): Promise<void> {
  const rec = { ...p, created_at: p.created_at ?? new Date().toISOString() };
  if (isCloud()) {
    try { const sb = getSupabase()!; const { error } = await sb.from("community_posts").insert(rec); if (error) throw error; return; } catch { /* fall through */ }
  }
  const all = localPosts(); all.push(rec); saveLocalPosts(all);
}

// --- leads (property exchange, NO commissions) ------------------------------
const LS_LEADS = "beacon_community_leads";
function localLeads(): CommunityLead[] { try { const r = localStorage.getItem(LS_LEADS); if (r) return JSON.parse(r); } catch { /* */ } return []; }
function saveLocalLeads(a: CommunityLead[]) { try { localStorage.setItem(LS_LEADS, JSON.stringify(a)); } catch { /* */ } }

export async function listLeads(): Promise<CommunityLead[]> {
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { data, error } = await sb.from("community_leads").select("*").order("created_at", { ascending: false });
      if (error) throw error;
      if (data) return data as CommunityLead[];
    } catch { /* fall through */ }
  }
  return localLeads().slice().reverse();
}
export async function addLead(l: CommunityLead): Promise<void> {
  const rec = { ...l, created_at: l.created_at ?? new Date().toISOString() };
  if (isCloud()) {
    try { const sb = getSupabase()!; const { error } = await sb.from("community_leads").insert(rec); if (error) throw error; return; } catch { /* fall through */ }
  }
  const all = localLeads(); all.push(rec); saveLocalLeads(all);
}

// --- requests (course / feature voting) -------------------------------------
const LS_REQ = "beacon_community_requests";
const LS_VOTED = "beacon_voted_requests";
function localReqs(): CommunityRequest[] { try { const r = localStorage.getItem(LS_REQ); if (r) return JSON.parse(r); } catch { /* */ } return []; }
function saveLocalReqs(a: CommunityRequest[]) { try { localStorage.setItem(LS_REQ, JSON.stringify(a)); } catch { /* */ } }
export function hasVoted(id: string): boolean { try { return (JSON.parse(localStorage.getItem(LS_VOTED) || "[]") as string[]).includes(id); } catch { return false; } }
function markVoted(id: string) { try { const v = JSON.parse(localStorage.getItem(LS_VOTED) || "[]") as string[]; if (!v.includes(id)) { v.push(id); localStorage.setItem(LS_VOTED, JSON.stringify(v)); } } catch { /* */ } }

export async function listRequests(): Promise<CommunityRequest[]> {
  let rows: CommunityRequest[] = [];
  if (isCloud()) {
    try { const sb = getSupabase()!; const { data, error } = await sb.from("community_requests").select("*"); if (error) throw error; if (data) rows = data as CommunityRequest[]; }
    catch { rows = localReqs(); }
  } else rows = localReqs();
  return rows.slice().sort((a, b) => (b.votes || 0) - (a.votes || 0));
}

export async function addRequest(r: CommunityRequest): Promise<void> {
  const rec = { ...r, votes: r.votes || 1, created_at: r.created_at ?? new Date().toISOString() };
  markVoted(rec.id); // author implicitly votes for their own
  if (isCloud()) {
    try { const sb = getSupabase()!; const { error } = await sb.from("community_requests").insert(rec); if (error) throw error; return; } catch { /* fall through */ }
  }
  const all = localReqs(); all.push(rec); saveLocalReqs(all);
}

/** Starter requests so the board is alive on day one (the roadmap, votable). */
export const STARTER_REQUESTS: CommunityRequest[] = [
  { id: "seed-section8-basics", kind: "course", title: "Section 8 & the Voucher Program", detail: "How Housing Choice Vouchers work and what it means to be an approved owner.", votes: 14, status: "planned" },
  { id: "seed-four-gates", kind: "course", title: "The Four Compliance Gates", detail: "NSPIRE, rent reasonableness, PHA/program, and legal/title — deep dive.", votes: 11, status: "planned" },
  { id: "seed-dd-leverage", kind: "course", title: "Turning Inspection into Leverage", detail: "Convert documented defects into a justified, profitable revised offer.", votes: 9, status: "open" },
  { id: "seed-syndication-101", kind: "course", title: "Syndication 101 — Pooling Capital Legally", detail: "Reg D 506(b), the working-partners model, and sponsor economics.", votes: 8, status: "open" },
  { id: "seed-fl-insurance", kind: "course", title: "Florida Insurance, De-risked", detail: "Stress-testing premium spikes and what underwriters look for.", votes: 6, status: "open" },
  { id: "seed-feat-excel", kind: "feature", title: "Export the cap table & worksheet to Excel", detail: "One-click XLSX of the deal + syndicate cap table.", votes: 7, status: "open" },
  { id: "seed-feat-ghl2way", kind: "feature", title: "GoHighLevel two-way sync", detail: "Push and pull — a stage change in GHL updates the deal in Beacon.", votes: 5, status: "open" },
];

/** Seed the starter requests once (idempotent; never resets existing votes). */
export async function seedStarterRequests(): Promise<void> {
  try { if (localStorage.getItem("beacon_requests_seeded")) return; } catch { /* */ }
  // Always seed the local fallback so the board is populated even before the
  // cloud table exists; also upsert to cloud (idempotent, never resets votes).
  const all = localReqs(); const ids = new Set(all.map((r) => r.id));
  const add = STARTER_REQUESTS.filter((r) => !ids.has(r.id));
  if (add.length) saveLocalReqs([...all, ...add]);
  if (isCloud()) {
    try { const sb = getSupabase()!; await sb.from("community_requests").upsert(STARTER_REQUESTS, { onConflict: "id", ignoreDuplicates: true }); } catch { /* */ }
  }
  try { localStorage.setItem("beacon_requests_seeded", "1"); } catch { /* */ }
}

/** One vote per device. Returns the new vote count (or null if already voted). */
export async function voteRequest(id: string): Promise<number | null> {
  if (hasVoted(id)) return null;
  if (isCloud()) {
    try {
      const sb = getSupabase()!;
      const { data } = await sb.from("community_requests").select("votes").eq("id", id).single();
      const next = ((data?.votes as number) || 0) + 1;
      const { error } = await sb.from("community_requests").update({ votes: next }).eq("id", id);
      if (error) throw error;
      markVoted(id); return next;
    } catch { /* fall through to local */ }
  }
  const all = localReqs(); const r = all.find((x) => x.id === id); if (!r) return null;
  r.votes = (r.votes || 0) + 1; saveLocalReqs(all); markVoted(id); return r.votes;
}
